import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroCarousel from "@/components/feature/HeroCarousel";
import MediaCard from "@/components/base/MediaCard";
import { mediaList } from "@/mocks/media";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const sortByDate = (a: { updatedAt: string }, b: { updatedAt: string }) =>
  b.updatedAt.localeCompare(a.updatedAt);

export default function Home() {
  useDocumentTitle(
    "光棍影院-最新电影与电视剧在线观看平台",
    "光棍影院为您提供丰富的电影和电视剧资源，涵盖多种类型和题材，支持在线播放和详细剧情介绍，满足您的观影需求。"
  );

  const featured = mediaList.filter((item) => item.backdrop).slice(0, 5);
  const latestMovies = mediaList
    .filter((item) => item.type === "movie")
    .sort(sortByDate)
    .slice(0, 6);
  const latestTv = mediaList
    .filter((item) => item.type === "tv")
    .sort(sortByDate)
    .slice(0, 6);
  const hotList = [...mediaList].sort((a, b) => b.views - a.views).slice(0, 8);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <h1 className="sr-only">光棍影院 - 最新电影与电视剧在线观看平台</h1>

        <HeroCarousel items={featured} />

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            <Link
              to="/category/movie"
              className="group relative overflow-hidden rounded-lg bg-foreground-950 p-6 md:p-8 flex items-center gap-5"
            >
              <div
                className="absolute inset-0 opacity-30 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{
                  backgroundImage:
                    "url('https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theater%20with%20a%20large%20glowing%20screen%20and%20silhouette%20seats%2Ccinematic%20wide%20still%2C%20dramatic%20lighting%2C%20deep%20blue%20color%20grading%2C%20atmospheric%20haze%2C%20high%20detail%2C%20no%20text%20or%20letters&width=800&height=400&seq=ggyx-cat-movie&orientation=landscape')",
                }}
              />
              <div className="relative z-10 w-14 h-14 md:w-16 md:h-16 rounded-lg bg-accent-500 flex items-center justify-center shrink-0">
                <i className="ri-movie-2-line text-2xl md:text-3xl text-foreground-950"></i>
              </div>
              <div className="relative z-10">
                <h2 className="text-xl md:text-2xl font-bold text-background-50 font-heading">
                  电影频道
                </h2>
                <p className="text-sm text-background-300 mt-1">
                  动作、科幻、爱情、悬疑全类型覆盖
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-accent-400 mt-2">
                  进入分类
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/category/tv"
              className="group relative overflow-hidden rounded-lg bg-primary-950 p-6 md:p-8 flex items-center gap-5"
            >
              <div
                className="absolute inset-0 opacity-30 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{
                  backgroundImage:
                    "url('https://readdy.ai/api/search-image?query=Cozy%20living%20room%20at%20night%20with%20a%20glowing%20tv%20and%20warm%20ambient%20light%2Ccinematic%20wide%20still%2C%20dramatic%20lighting%2C%20deep%20blue%20color%20grading%2C%20atmospheric%20haze%2C%20high%20detail%2C%20no%20text%20or%20letters&width=800&height=400&seq=ggyx-cat-tv&orientation=landscape')",
                }}
              />
              <div className="relative z-10 w-14 h-14 md:w-16 md:h-16 rounded-lg bg-accent-500 flex items-center justify-center shrink-0">
                <i className="ri-tv-2-line text-2xl md:text-3xl text-foreground-950"></i>
              </div>
              <div className="relative z-10">
                <h2 className="text-xl md:text-2xl font-bold text-background-50 font-heading">
                  电视剧频道
                </h2>
                <p className="text-sm text-background-300 mt-1">
                  热门剧集、古装都市、全程追更
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-accent-400 mt-2">
                  进入分类
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                基于站内访问热度精选的 Top 8 影视作品
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {hotList.map((item, i) => (
              <MediaCard key={item.id} item={item} showRank={i + 1} />
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2">
                <i className="ri-film-line text-primary-600"></i>
                最新电影
              </h2>
              <p className="text-sm text-foreground-500 mt-1">刚刚上线的精选电影</p>
            </div>
            <Link
              to="/category/movie"
              className="text-sm font-medium text-primary-600 hover:text-accent-600 inline-flex items-center gap-1 whitespace-nowrap"
            >
              更多电影
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {latestMovies.map((item) => (
              <MediaCard key={item.id} item={item} compact />
            ))}
          </div>

          <div className="flex items-end justify-between mb-6 mt-12">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading flex items-center gap-2">
                <i className="ri-tv-line text-primary-600"></i>
                最新电视剧
              </h2>
              <p className="text-sm text-foreground-500 mt-1">正在热播与最新更新的剧集</p>
            </div>
            <Link
              to="/category/tv"
              className="text-sm font-medium text-primary-600 hover:text-accent-600 inline-flex items-center gap-1 whitespace-nowrap"
            >
              更多剧集
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {latestTv.map((item) => (
              <MediaCard key={item.id} item={item} compact />
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-foreground-950">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex w-12 h-12 rounded-lg bg-primary-500 items-center justify-center mb-5">
              <i className="ri-film-line text-2xl text-accent-400"></i>
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
              关于光棍影院
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed">
              光棍影院是一个专注最新电影与电视剧的内容门户，致力于用清晰的分类、快速的搜索与多维度的筛选，帮你更轻松地找到想看的作品。我们收录丰富的剧情资料与元信息，让每一次浏览都更有收获。
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg bg-background-50/5 border border-background-800 p-5">
                <i className="ri-movie-line text-2xl text-accent-400"></i>
                <p className="mt-2 text-sm font-semibold text-background-50">海量片库</p>
                <p className="text-xs text-background-400 mt-1">电影 · 电视剧 · 全类型覆盖</p>
              </div>
              <div className="rounded-lg bg-background-50/5 border border-background-800 p-5">
                <i className="ri-filter-3-line text-2xl text-accent-400"></i>
                <p className="mt-2 text-sm font-semibold text-background-50">智能筛选</p>
                <p className="text-xs text-background-400 mt-1">类型 · 地区 · 年份 · 排序</p>
              </div>
              <div className="rounded-lg bg-background-50/5 border border-background-800 p-5">
                <i className="ri-bookmark-line text-2xl text-accent-400"></i>
                <p className="mt-2 text-sm font-semibold text-background-50">本地收藏</p>
                <p className="text-xs text-background-400 mt-1">无需登录 · 一键收藏</p>
              </div>
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 px-6 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}